import { useCallback, useEffect, useRef, useState } from 'react';
import { ShipmentTracking } from '@/components/logistics/ShipmentTracking';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { formatCOP, formatDate, statusLabel, statusColor } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { InvoiceSection } from '@/components/InvoiceSection';
import { ProductImage } from '@/components/ProductImage';
import { resolveImageUrl } from '@/components/ImageUploader';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Star, Search, ChevronLeft, ChevronRight, X, CheckCircle } from 'lucide-react';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';
import { BUYER } from '@/constants/testIds';

export default function BuyerOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0); // 0-indexed
  const [reviewItem, setReviewItem] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  // Estado del modal "Confirmar recepción" — mismo estilo que el resto de la
  // app (AlertDialog de shadcn) en lugar del window.confirm nativo.
  const [confirmOrderId, setConfirmOrderId] = useState(null);
  const PAGE_SIZE = 15;

  useSEO({
    title: 'Mis pedidos | AutoMarket Colombia',
    description: 'Consulta el estado de tus pedidos, tracking y califica los productos recibidos.',
    canonical: '/mis-pedidos',
  });

  // Los filtros se aplican al pulsar «Buscar»; la paginación recarga sola.
  const filtersRef = useRef({ q, status, dateFrom, dateTo });
  filtersRef.current = { q, status, dateFrom, dateTo };
  const fetchOrders = useCallback(() => {
    const f = filtersRef.current;
    const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    if (f.q.trim()) params.q = f.q.trim();
    if (f.status) params.status = f.status;
    if (f.dateFrom) params.date_from = f.dateFrom;
    if (f.dateTo) params.date_to = f.dateTo;
    return api.get('/orders/me', { params }).then(({ data }) => {
      setOrders(data.items || []);
      setTotal(data.total || 0);
    });
  }, [page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const applyFilters = () => { setPage(0); fetchOrders(); };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const submitReview = async () => {
    try {
      await api.post('/reviews', {
        order_id: reviewItem.order_id,
        product_id: reviewItem.product_id,
        rating, comment,
      });
      toast.success('Calificación enviada');
      setReviewItem(null); setComment(''); setRating(5);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const confirmReceived = async (order_id) => {
    try {
      await api.post(`/orders/${order_id}/confirm-received`);
      toast.success('¡Gracias! Recepción confirmada');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo confirmar');
    } finally {
      setConfirmOrderId(null);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Mi cuenta</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Mis pedidos</h1>
        <Link to="/mis-reembolsos-envio" className="mt-2 inline-block text-sm font-semibold text-orange-600 hover:underline" data-testid="buyer-shipping-refunds-link">
          Ver mis reembolsos de envío →
        </Link>

        {/* Filters bar */}
        <div className="mt-6 border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="buyer-orders-search"
              placeholder="Buscar por ID de orden o nombre del producto"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              className="pl-9 rounded-none"
            />
          </div>
          <div className="min-w-[160px]">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Estado</label>
            <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="buyer-orders-status-filter" className="rounded-none">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="paid">Pagado</SelectItem>
                <SelectItem value="shipped">Enviado</SelectItem>
                <SelectItem value="delivered">Entregado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Desde</label>
            <Input
              data-testid="buyer-orders-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-none"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Hasta</label>
            <Input
              data-testid="buyer-orders-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-none"
            />
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="buyer-orders-apply-filters"
              onClick={applyFilters}
              className="rounded-none bg-slate-900 hover:bg-slate-800 h-10"
            >
              Aplicar
            </Button>
            {(q || status || dateFrom || dateTo) && (
              <Button
                data-testid="buyer-orders-clear-filters"
                variant="outline"
                onClick={() => {
                  setQ(''); setStatus(''); setDateFrom(''); setDateTo('');
                  setPage(0);
                  // Trigger fetch with cleared state on next tick
                  setTimeout(() => fetchOrders(), 0);
                }}
                className="rounded-none h-10"
                title="Limpiar filtros"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
          <span data-testid="buyer-orders-count">
            {total === 0 ? 'Sin pedidos' : `${total} pedido${total === 1 ? '' : 's'} encontrado${total === 1 ? '' : 's'}`}
          </span>
          {totalPages > 1 && (
            <span data-testid="buyer-orders-page-info">Página {page + 1} de {totalPages}</span>
          )}
        </div>

        {orders.length === 0 && (
          <div className="mt-4 border border-slate-200 p-12 text-center text-slate-500 bg-slate-50">
            {total === 0 && (q || status || dateFrom || dateTo)
              ? 'No hay pedidos que coincidan con los filtros.'
              : 'Aún no tienes pedidos.'}
          </div>
        )}

        <div className="mt-4 space-y-4">
          {orders.map((o) => (
            <div data-testid={BUYER.orderRow(o.order_id)} key={o.order_id} className="border border-slate-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm min-w-0">
                  <span className="font-mono text-slate-500 break-all">{o.order_id}</span>
                  <span className="text-slate-500">{formatDate(o.created_at)}</span>
                </div>
                <div className="flex items-center gap-4">
                  <Badge className={`rounded-none border ${statusColor[o.status] || ''}`}>
                    {statusLabel[o.status] || o.status}
                  </Badge>
                  <div className="text-right">
                    <div className="font-display font-bold text-slate-900">{formatCOP(o.total)}</div>
                    {['configured', 'automarket'].includes(o.shipping?.mode) && (
                      <div className="text-[11px] text-slate-500" data-testid={`buyer-order-shipping-${o.order_id}`}>
                        incl. envío {o.shipping_amount ? formatCOP(o.shipping_amount) : 'gratis'} · {o.shipping.days_min}–{o.shipping.days_max} días
                      </div>
                    )}
                    {o.logistics_mode === 'automarket' && <ShipmentTracking orderId={o.order_id} />}
                  </div>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {o.items.map((it) => (
                  <div key={it.product_id} className="flex items-center gap-4 p-4">
                    <Link
                      to={`/producto/${it.product_id}`}
                      data-testid={`buyer-order-item-link-${it.product_id}`}
                      className="h-16 w-16 shrink-0 overflow-hidden hover:opacity-80 transition-opacity"
                      aria-label={`Ver detalle de ${it.name}`}
                    >
                      <ProductImage
                        src={resolveImageUrl(it.image)}
                        alt={it.name || ''}
                        className="h-full w-full object-cover"
                        iconSize="h-6 w-6"
                        testid={`buyer-order-item-image-${it.product_id}`}
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/producto/${it.product_id}`}
                        data-testid={`buyer-order-item-name-${it.product_id}`}
                        className="font-semibold text-slate-900 hover:text-blue-700 hover:underline transition-colors truncate block"
                      >
                        {it.name}
                      </Link>
                      <div className="text-sm text-slate-500">{it.qty} × {formatCOP(it.price)}</div>
                    </div>
                    {o.status === 'delivered' && (
                      <Button
                        data-testid={BUYER.reviewBtn(it.product_id)}
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => setReviewItem({ order_id: o.order_id, product_id: it.product_id, name: it.name })}
                      >
                        <Star className="mr-1 h-3.5 w-3.5" /> Calificar
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {o.tracking_code && (
                <div className="px-5 py-3 border-t border-slate-200 text-sm bg-slate-50">
                  <span className="font-bold uppercase text-xs tracking-[0.2em] text-slate-500 mr-2">Tracking</span>
                  <span className="font-mono">{o.tracking_code}</span>
                </div>
              )}
              {o.status === 'delivered' && !o.buyer_confirmed_at && (
                <div className="px-5 py-3 border-t border-slate-200 bg-emerald-50 flex items-center justify-between gap-2">
                  <span className="text-xs text-emerald-900">¿Ya recibiste este pedido?</span>
                  <Button
                    data-testid={`buyer-order-confirm-${o.order_id}`}
                    onClick={() => setConfirmOrderId(o.order_id)}
                    size="sm"
                    className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white"
                  >
                    <CheckCircle className="mr-1 h-3.5 w-3.5" /> Confirmar recepción
                  </Button>
                </div>
              )}
              {o.buyer_confirmed_at && (
                <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 inline-flex items-center gap-1">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> Recepción confirmada
                </div>
              )}
              <InvoiceSection orderId={o.order_id} role="buyer" />
            </div>
          ))}
        </div>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2" data-testid="buyer-orders-pagination">
            <Button
              data-testid="buyer-orders-prev-page"
              variant="outline"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-none"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
            </Button>
            <span className="text-sm text-slate-600 px-3">
              {page + 1} / {totalPages}
            </span>
            <Button
              data-testid="buyer-orders-next-page"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-none"
            >
              Siguiente <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        <Dialog open={!!reviewItem} onOpenChange={(o) => { if (!o) setReviewItem(null); }}>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle>Calificar producto</DialogTitle>
            </DialogHeader>
            <div className="mt-2 text-sm text-slate-700">{reviewItem?.name}</div>
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Calificación</div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setRating(n)}>
                    <Star className={`h-7 w-7 ${n <= rating ? 'fill-orange-500 text-orange-500' : 'text-slate-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tu opinión..."
              rows={3}
              className="mt-4 w-full border border-slate-300 p-2 outline-none focus:border-slate-900"
            />
            <Button onClick={submitReview} className="rounded-none bg-slate-900 hover:bg-slate-800 mt-4">
              Enviar calificación
            </Button>
          </DialogContent>
        </Dialog>

        {/* Confirmar recepción — reemplaza el window.confirm nativo por un
             AlertDialog de shadcn con el look de la app. No menciona pagos ni
             liberación al vendedor (esa es lógica interna del sistema). */}
        <AlertDialog
          open={!!confirmOrderId}
          onOpenChange={(open) => { if (!open) setConfirmOrderId(null); }}
        >
          <AlertDialogContent
            data-testid="buyer-confirm-received-dialog"
            className="rounded-none border border-slate-200 max-w-md"
          >
            <AlertDialogHeader className="items-center text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full border-2 border-emerald-500">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <AlertDialogTitle className="font-display text-xl font-black tracking-tight text-slate-900">
                ¿Confirmas que recibiste este pedido?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-500">
                Al confirmar, finalizaremos tu pedido.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-4 flex-col-reverse sm:flex-row sm:justify-center gap-2">
              <AlertDialogCancel
                data-testid="buyer-confirm-received-cancel"
                className="rounded-none border-slate-300"
              >
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="buyer-confirm-received-confirm"
                onClick={() => confirmOrderId && confirmReceived(confirmOrderId)}
                className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                <CheckCircle className="mr-2 h-4 w-4" /> Confirmar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
